import { useState, useEffect, useCallback } from 'react';
import { doctorService } from '../services/doctorService';

export interface DoctorListItem {
  id: number;
  first_name: string;
  last_name: string;
  alias?: string;
  specialty?: { id: number; name: string };
  phone?: string;
  mobile?: string;
  is_active: boolean;
}

export const useDoctorList = () => {
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 15,
  });
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const fetchDoctors = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: pagination.perPage,
        ...(search && { search }),
      };

      const response = await doctorService.getAll(params);

      setDoctors(response.data || []);
      setPagination(prev => ({
        ...prev,
        currentPage: response.current_page || page,
        totalPages: response.last_page || 1,
        total: response.total || 0,
      }));
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setError('Error loading doctors');
    } finally {
      setLoading(false);
    }
  }, [pagination.perPage]);

  const searchDoctors = useCallback(async (term: string) => {
    if (!term.trim()) {
      await fetchDoctors(1, '');
      return;
    }
    setLoading(true);
    try {
      const results = await doctorService.search(term);
      setDoctors(results || []);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalPages: 1,
        total: results?.length || 0,
      }));
    } catch (err) {
      console.error('Error searching doctors:', err);
      setError('Error searching doctors');
    } finally {
      setLoading(false);
    }
  }, [fetchDoctors]);

  const deleteDoctor = useCallback(async (id: number) => {
    setPendingDeleteId(id);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteId == null) return;

    setLoading(true);
    try {
      await doctorService.delete(pendingDeleteId);
      await fetchDoctors(pagination.currentPage);
    } catch (err) {
      console.error('Error deleting doctor:', err);
      setError('Error deleting doctor');
    } finally {
      setLoading(false);
      setPendingDeleteId(null);
    }
  }, [fetchDoctors, pagination.currentPage, pendingDeleteId]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchDoctors(page);
    }
  }, [fetchDoctors, pagination.totalPages]);

  return {
    doctors,
    loading,
    error,
    pagination,
    pendingDeleteId,
    fetchDoctors,
    searchDoctors,
    deleteDoctor,
    cancelDelete,
    confirmDelete,
    goToPage,
  };
};
